package Structs

import Utils.PayMethod
import Utils.getJsonWithConfig
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import myName

/** 1 = cash, 2 = checks. A deposit never mixes the two — separate bank slips. */
const val DEPOSIT_SOURCE_CASH = 1
const val DEPOSIT_SOURCE_CHECK = 2

/**
 * One payment row inside a deposit.
 *
 * A payment row is a JSON array element inside its parent document's
 * `payment_data_raw`. Prefer [payment_inner_id] when set (> 0); otherwise
 * fall back to [payment_index] (array position — the legacy address).
 * `doc_type` follows the same convention as `closePayment`: 7 = receipt
 * (ClientPay), 1 = tax note with payment (ClientTaxNote).
 *
 * The remaining fields are a denormalised snapshot taken at deposit time so a
 * deposit can be displayed and exported without re-reading every document.
 */
@myName("DepositLine")
@Serializable
data class DepositLine(
    val doc_type: Int = 7,
    val doc_id: Int = 0,
    val payment_index: Int = 0,
    val payment_inner_id: Int = 0,
    val sup: Int = 0,
    val pay_method: Int = 0,
    val value: Float = 0f,
    val check_number: String = "",
    val check_date: String = "",
    val bank_id: String = "",
    val bank_branch: String = "",
    val bank_number: String = "",
    val client_id: Int = -1,
    val client_name: String = "",
    val document_date: String = "",
) {
    /**
     * A computed getter, not a stored property, and deliberately so: the JS name
     * is published by an InteropShims `defGet`, which installs a getter-only
     * accessor on the prototype. A stored property of the same name is written by
     * the constructor, and that write hits the accessor and throws
     * "Cannot set property payMethod ... which has only a getter" — killing every
     * construction of this class in the browser. No backing field, no conflict.
     */
    val payMethod: PayMethod get() = PayMethod.CASH.convert(pay_method)

    @myName("isCheck")
    fun isCheck(): Boolean = payMethod == PayMethod.CHECK || payMethod == PayMethod.FAST_CHECK

    fun toJson(): JsonObject {
        val map: MutableMap<String, JsonPrimitive> = mutableMapOf()
        map["doc_type"] = JsonPrimitive(doc_type.toString())
        map["doc_id"] = JsonPrimitive(doc_id.toString())
        map["payment_index"] = JsonPrimitive(payment_index.toString())
        if (payment_inner_id > 0) map["payment_inner_id"] = JsonPrimitive(payment_inner_id.toString())
        map["sup"] = JsonPrimitive(sup.toString())
        map["pay_method"] = JsonPrimitive(pay_method.toString())
        map["value"] = JsonPrimitive(value.toString())
        map["check_number"] = JsonPrimitive(check_number)
        map["check_date"] = JsonPrimitive(check_date)
        map["bank_id"] = JsonPrimitive(bank_id)
        map["bank_branch"] = JsonPrimitive(bank_branch)
        map["bank_number"] = JsonPrimitive(bank_number)
        map["client_id"] = JsonPrimitive(client_id.toString())
        map["client_name"] = JsonPrimitive(client_name)
        map["document_date"] = JsonPrimitive(document_date)
        return JsonObject(map)
    }

    companion object {
        /**
         * Tolerant on purpose: the column is nullable and blank for deposits
         * created before this feature, and `acceptArgs` escapes single quotes on
         * the way in (same reason [PaymentData.createFromJson] un-escapes them).
         */
        fun createFromJson(js: String?): List<DepositLine> {
            if (js.isNullOrBlank()) return listOf()
            return try {
                val arr = getJsonWithConfig().parseToJsonElement(js.replace("\\'", "'")) as JsonArray
                arr.map { getJsonWithConfig().decodeFromJsonElement(serializer(), it) }
            } catch (e: Exception) {
                listOf()
            }
        }

        fun toJsonArrayString(lines: List<DepositLine>): String {
            return if (lines.isEmpty()) {
                "[]"
            } else {
                val payload = lines.map {
                    Json.encodeToJsonElement(JsonObject.serializer(), it.toJson()).toString()
                }.joinToString(",")
                "[$payload]"
            }
        }
    }
}

/**
 * A bank deposit (הפקדה): one bank slip covering a bundle of cash or checks.
 *
 * [id] is the per-company running number shown to the user; in Hashavshevet
 * movein it is אסמכתא1 for cash and אסמכתא2 for checks (checks put the check
 * number in אסמכתא1). [number] is the global surrogate key.
 * [bank_account_id] resolves against [HashavshevetS.bankAccounts].
 */
@myName("Deposit")
@Serializable
data class Deposit(
    val id: Int,
    val number: Int = 0,
    val company_id: Int = -1,
    val deposit_date: String = "",
    val bank_account_id: Int = 0,
    val source_type: Int = DEPOSIT_SOURCE_CASH,
    val total: Float = 0f,
    val lines_count: Int = 0,
    val lines_raw: String? = null,
    val status: Int = 1,
    val exported_at: String? = null,
    val notes: String = "",
    val user_name: String = "",
    val created_at: String = "",
    val updated_at: String = "",
) {
    // Parsed once, then read through a computed getter: the shimmed `lines` name
    // must not also be a backing field the constructor writes. See
    // [DepositLine.payMethod].
    private val parsedLines: List<DepositLine> = DepositLine.createFromJson(lines_raw)

    val lines: List<DepositLine> get() = parsedLines

    @myName("isActive")
    fun isActive(): Boolean = status == 1

    @myName("getSourceLabel")
    fun getSourceLabel(): String = if (source_type == DEPOSIT_SOURCE_CHECK) "צ'קים" else "מזומן"

    /** Sum of the stored lines, for cross-checking against the stored [total]. */
    @myName("getLinesTotal")
    fun getLinesTotal(): Float = lines.sumOf { it.value.toDouble() }.toFloat()
}
